import { Alert, Button, Card, Group, NumberInput, Stack, Switch, Text, Title } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageHeader } from "@how/ui";
import { supabase } from "../../lib/supabase";

interface MessagingPolicy {
  readonly approvalRequired: boolean;
  readonly approvalThreshold: number;
  readonly provider: string;
}

async function readPolicy(): Promise<MessagingPolicy> {
  const { data, error } = await supabase.rpc("platform_messaging_policy");
  if (error) throw error;
  const d = data as unknown as Partial<MessagingPolicy>;
  return {
    approvalRequired: d.approvalRequired ?? true,
    approvalThreshold: d.approvalThreshold ?? 50,
    provider: d.provider ?? "simulated",
  };
}

/**
 * Platform settings (super admin): the advanced switches the owner decided on 2026-09-25. Today, the campaign
 * approval rule: whether a second signature is required at all, and above how many people. Planning Center has no
 * approval step; a platform that wants to match it switches the requirement off here.
 */
export function PlatformSettingsPage() {
  const client = useQueryClient();
  const policy = useQuery({ queryKey: ["platform-messaging-policy"], queryFn: readPolicy });
  const [required, setRequired] = useState(true);
  const [threshold, setThreshold] = useState(50);
  useEffect(() => {
    if (policy.data !== undefined) {
      setRequired(policy.data.approvalRequired);
      setThreshold(policy.data.approvalThreshold);
    }
  }, [policy.data]);
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("set_platform_messaging_policy", {
        p_approval_required: required,
        p_approval_threshold: threshold,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["platform-messaging-policy"] });
    },
  });
  const dirty =
    policy.data !== undefined &&
    (required !== policy.data.approvalRequired || threshold !== policy.data.approvalThreshold);

  return (
    <>
      <PageHeader
        title="Platform settings"
        description="Advanced switches that apply to every church. Change them knowingly; each one is audited."
      />
      <Card>
        <Stack gap="sm">
          <Title order={4}>Campaign approval</Title>
          <Text size="sm" c="dimmed" maw={640}>
            A campaign that would reach at least this many people needs a second person's approval,
            and never the author's own. Below the line, someone who may send goes out on their own
            authority. Switch the requirement off to match Planning Center, which has no approval
            step.
          </Text>
          <Switch
            label="Require a second signature above the threshold"
            checked={required}
            onChange={(event) => {
              setRequired(event.currentTarget.checked);
            }}
          />
          <NumberInput
            label="Threshold: people reached"
            description="0 means every campaign needs approval. 50 is the platform's default."
            min={0}
            max={1000000}
            disabled={!required}
            value={threshold}
            onChange={(value) => {
              setThreshold(typeof value === "number" ? value : 0);
            }}
            maw={280}
          />
          <Text size="xs" c="dimmed">
            Messaging provider on this environment: {policy.data?.provider ?? "…"}.
          </Text>
          {save.isError ? (
            <Alert color="red" variant="light">
              {save.error.message}
            </Alert>
          ) : null}
          <Group>
            <Button
              loading={save.isPending}
              disabled={!dirty}
              onClick={() => {
                save.mutate();
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Card>
    </>
  );
}
